// The signed-in console: its pages, by role (26 §3.2). Hiding a page is never the protection: the
// server checks every read and every command (ADM-004). App renders it once the admin has signed in
// with a second factor (ADM-002); the look harness renders it with a fixture client (decision record
// 0071), so the pictures are of these very pages.
import type { SupabaseClient } from '@supabase/supabase-js';
import { useState } from 'react';
import { can, type AdminRole } from './auth/roles';
import { AdminsPage } from './pages/Admins';
import { ClosuresPage } from './pages/Closures';
import { ConfigPage } from './pages/Config';
import { CoursesPage } from './pages/Courses';
import { HealthPage } from './pages/Health';
import { RoundInspectorPage } from './pages/RoundInspector';
import { SimulationPage } from './pages/Simulation';

export type Page =
  'health' | 'config' | 'admins' | 'simulation' | 'rounds' | 'courses' | 'closures';

const ROLES: readonly AdminRole[] = ['owner', 'moderator', 'support', 'analyst'];
/** The signed-in admin's role, when it is one of the four. */
export const roleOf = (r: string | null): AdminRole | null =>
  ROLES.includes(r as AdminRole) ? (r as AdminRole) : null;

export function Console({
  sb,
  role,
  onSignOut,
  initialPage = 'health',
}: {
  sb: SupabaseClient;
  role: string | null;
  onSignOut: () => void;
  initialPage?: Page;
}) {
  const [page, setPage] = useState<Page>(initialPage);
  const r = roleOf(role);
  return (
    <main>
      <h1>Fairway Life admin</h1>
      <nav aria-label="Pages">
        <button aria-current={page === 'health'} onClick={() => setPage('health')}>
          Health
        </button>
        <button aria-current={page === 'config'} onClick={() => setPage('config')}>
          Settings and approvals
        </button>
        {role === 'owner' && (
          <button aria-current={page === 'admins'} onClick={() => setPage('admins')}>
            Admin users
          </button>
        )}
        {r && can(r, 'simulation') && (
          <button aria-current={page === 'simulation'} onClick={() => setPage('simulation')}>
            Simulation
          </button>
        )}
        {r && can(r, 'round_inspector') && (
          <button aria-current={page === 'rounds'} onClick={() => setPage('rounds')}>
            Round inspector
          </button>
        )}
        {r && can(r, 'course_review') && (
          <button aria-current={page === 'courses'} onClick={() => setPage('courses')}>
            Courses to review
          </button>
        )}
        {/* 26 ADM-050, ADM-091: an owner's page, and the backup contact's while away mode is on; the server decides */}
        <button aria-current={page === 'closures'} onClick={() => setPage('closures')}>
          Closures
        </button>
        <button onClick={onSignOut}>Sign out</button>
      </nav>
      {page === 'health' && <HealthPage sb={sb} />}
      {page === 'config' && <ConfigPage sb={sb} />}
      {page === 'admins' && <AdminsPage sb={sb} />}
      {page === 'simulation' && <SimulationPage sb={sb} owner={role === 'owner'} />}
      {page === 'rounds' && <RoundInspectorPage sb={sb} />}
      {page === 'courses' && <CoursesPage sb={sb} />}
      {page === 'closures' && <ClosuresPage sb={sb} />}
    </main>
  );
}
